import { Button } from "@loot-os/ui/components/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@loot-os/ui/components/tooltip";
import { cn } from "@loot-os/ui/lib/utils";
import { Check, Copy, ListFilter } from "lucide-react";
import { type ReactNode, useState } from "react";

/**
 * A heading over the fields that share a subject. Bold and in the foreground color, the one
 * bright line in a block of muted labels, so the groups are found before any field is read.
 * The analytics event log's detail and the Shopify purchase detail share these, so an opened
 * row reads the same everywhere.
 */
export function DetailGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="min-w-0">
      <h3 className="mb-2 text-xs font-bold tracking-wider text-foreground uppercase">{title}</h3>
      <dl className="grid grid-cols-[minmax(7rem,max-content)_minmax(0,1fr)] gap-x-4 text-sm">
        {children}
      </dl>
    </section>
  );
}

/** What a field's filter button says and does. */
interface FieldFilter {
  label: string;
  onSelect: () => void;
}

/**
 * One label and its value, a dash when the row recorded none. Every line is as tall as the
 * icon buttons, so a field with actions and one without keep the same rhythm.
 */
export function DetailField({
  label,
  labelMono,
  mono,
  copy,
  filter,
  children,
}: {
  label: string;
  /** The label is itself a recorded key (a property name), printed as one. */
  labelMono?: boolean;
  mono?: boolean;
  /** What the copy button puts on the clipboard; no button without it. */
  copy?: string | null;
  filter?: FieldFilter | null;
  children: ReactNode;
}) {
  const empty =
    children === null || children === undefined || children === false || children === "";
  return (
    <>
      <dt
        className={cn(
          "py-0.5 leading-5 text-muted-foreground",
          labelMono && "font-mono text-xs leading-5",
        )}
      >
        {label}
      </dt>
      <dd className="flex min-w-0 items-start gap-1">
        <span
          className={cn(
            "min-w-0 py-0.5 leading-5 break-words",
            mono && "font-mono text-xs leading-5",
          )}
        >
          {empty ? <span className="text-muted-foreground">—</span> : children}
        </span>
        {!empty && (copy || filter) && (
          <span className="flex shrink-0">
            {copy && (
              <CopyButton text={copy} label={`Copy ${labelMono ? label : lowerFirst(label)}`} />
            )}
            {filter && (
              <IconAction label={filter.label} onClick={filter.onSelect}>
                <ListFilter />
              </IconAction>
            )}
          </span>
        )}
      </dd>
    </>
  );
}

function lowerFirst(text: string): string {
  return text.charAt(0).toLowerCase() + text.slice(1);
}

/** A quiet icon button that names itself on hover and to a screen reader. */
function IconAction({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            aria-label={label}
            className="text-muted-foreground hover:text-foreground"
            onClick={onClick}
          />
        }
      >
        {children}
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

/** Copies `text`, and says "Copied" for a moment after. */
export function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <IconAction label={copied ? "Copied" : label} onClick={() => void copy()}>
      {copied ? <Check /> : <Copy />}
    </IconAction>
  );
}
